import { useMutation } from '@apollo/client';
import { useState } from 'react';
import {
    ExternalFinancialEntity,
    ExternalFinancialEntityType,
    feInfoSchemaByFEType,
    feSnapshotInfoSchemaByFEType,
} from '../../financialEntities';
import { feConfigs } from '../../financialEntities/config';
import {
    AllFinancialEntitiesDocument,
    CreateFinancialEntityDocument,
} from '../../graphql/generated';
import { typecheck } from '../../typebox';
import { typedKeys } from '../../util/objects';
import { getErrorLogger } from '../errorLogger';
import { isInvalidForm } from './isInvalidForm';

export const useAddAccountManual = ({
    type,
    onComplete,
}: {
    type: ExternalFinancialEntityType;
    onComplete?: (entity: ExternalFinancialEntity) => Promise<void>;
}) => {
    const logError = getErrorLogger();

    // Get form config
    const config = feConfigs[type];

    // Construct mutation hook
    const [createFinancialEntity] = useMutation(CreateFinancialEntityDocument, {
        refetchQueries: [AllFinancialEntitiesDocument],
        // This awaitRefetchQueries is needed to avoid race conditions.
        // Ref: https://github.com/plentyfinancial/plenty-app/pull/1073#discussion_r1367693475
        awaitRefetchQueries: true,
    });

    // Construct initial form state
    const initialState = config.addForm.fields.reduce(
        (initialState, field) => {
            initialState[field.name] = field.defaultValue;

            return initialState;
        },
        {} as Record<string, any>
    );
    const [form, setForm] = useState(initialState);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const changeField = (name: string, value: any) => {
        const hiddenFieldNames = config.addForm.fields
            .filter(
                (field) =>
                    field.isVisible?.({ ...form, [name]: value }) === false
            )
            .map((field) => field.name);

        const updatedForm = {
            ...form,
            [name]: value,
            // Clear hidden fields
            ...Object.fromEntries(
                hiddenFieldNames.map((name) => [name, undefined])
            ),
            // HACK: Set balance to 0 if `isPaidOffMonthly` is true
            ...(name === 'isPaidOffMonthly' && value === 'true'
                ? { balance: 0 }
                : {}),
        };

        // HACK: Default `currentPayment` to `minimumPayment`
        // TODO: Considering adding this as an abstraction on FieldConfig.
        if (
            name === 'minimumPayment' &&
            'currentPayment' in form &&
            form.minimumPayment === form.currentPayment
        ) {
            return setForm({
                ...updatedForm,
                minimumPayment: value,
                currentPayment: value,
            });
        }

        setForm(updatedForm);
    };

    // Simple required/optional validation
    const invalid = isInvalidForm(form, config.addForm.fields);

    // Construct submit function
    const submit = async () => {
        if (invalid) return;

        // Sanitize form values
        typedKeys(form).forEach((k) => {
            // TODO: Update boolean-relevant form inputs to actually export true/false
            if (form[k] === 'true') form[k] = true;
            if (form[k] === 'false') form[k] = false;
        });

        const { name, balance, ...rest } = form;

        const info = {
            feType: type,
            ...Object.fromEntries(
                Object.entries(rest).filter(
                    ([k]) => !config.snapshotFieldNames.includes(k)
                )
            ),
        };
        const infoSchema = feInfoSchemaByFEType[type];
        typecheck(infoSchema, info, (errors) => {
            const e = new Error('FE info failed typecheck');
            logError(e, { errors });
            throw e;
        });

        const snapshotInfo = {
            feType: type,
            ...Object.fromEntries(
                Object.entries(rest).filter(([k]) =>
                    config.snapshotFieldNames.includes(k)
                )
            ),
        };
        const snapshotInfoSchema = feSnapshotInfoSchemaByFEType[type];
        typecheck(snapshotInfoSchema, snapshotInfo, (errors) => {
            const e = new Error('FE snapshot info failed typecheck');
            logError(e, { snapshotInfo, errors });
            throw e;
        });

        const variables = {
            name,
            type,
            balance,
            info,
            snapshotInfo,
        };

        setIsSubmitting(true);

        const res = await createFinancialEntity({
            variables,
        });

        if (res.data?.createFinancialEntity?.financialEntity) {
            const newFE = res.data.createFinancialEntity.financialEntity;
            onComplete?.(newFE as ExternalFinancialEntity) // TODO: improve typing
                .finally(() => {
                    setIsSubmitting(false);
                });
            return { financialEntityId: newFE.id };
        } else {
            setIsSubmitting(false);
        }
    };

    return {
        addForm: config.addForm,
        title: config.title,
        form,
        setForm,
        changeField,
        submit,
        isSubmitting,
        invalid,
    };
};
